import { useEffect } from 'react';

import type { ClickAwayEventHandler } from 'shared/types/clickAwayEventHandler';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface DocumentFallback extends Document {
  attachEvent?: (event: string, listener: EventListener) => boolean;
  detachEvent?: (event: string, listener: EventListener) => void;
}

type HandlerNode = {
  bearingElement: HTMLElement | HTMLElement[];
  handler: ClickAwayEventHandler;
  ignoreClassNames: string[];
  key: string;
};

const handlers: HandlerNode[] = [];

const hasIntersections = (arr1: string[], arr2: string[]) =>
  arr1.reduce((result, element) => (!result && arr2.includes(element) ? true : result), false);

const hasParent = (target: HTMLElement, ignoreClassNames: string[]) =>
  ignoreClassNames.some((className) => target.closest(`.${className}`));

const checkElement = (element: HTMLElement | null, target: HTMLElement, ignoreClassNames: string[]): boolean => {
  const targetClassNames = target.className.split(' ');
  if (
    ignoreClassNames.length > 0 &&
    (hasIntersections(targetClassNames, ignoreClassNames) || hasParent(target, ignoreClassNames))
  ) {
    return false;
  }
  return !element?.contains(target);
};

const getLastHandlerNode = () => {
  if (handlers.length > 0) {
    return handlers[handlers.length - 1];
  }
};

export const clickAwayUnsubscribe = (key: string) => {
  const nodeIndex = handlers.findIndex((node) => node.key === key);
  if (nodeIndex > -1) {
    handlers.splice(nodeIndex, 1);
  }
};

export const clickAwaySubscribe = (
  handler: ClickAwayEventHandler,
  bearingElement: HTMLElement | HTMLElement[],
  ignoreClassNames: string[] = [],
) => {
  const key = `${Date.now()}`;
  const handlerNode: HandlerNode = { bearingElement, handler, ignoreClassNames, key };
  handlers.push(handlerNode);
  return key;
};

const handleClick: ClickAwayEventHandler = (event) => {
  const handlerNode = getLastHandlerNode();

  if (!handlerNode) {
    return;
  }

  const { bearingElement, handler, ignoreClassNames } = handlerNode;

  if (event.target instanceof HTMLElement) {
    if (bearingElement instanceof Array) {
      if (bearingElement.every((item) => checkElement(item, event.target as HTMLElement, ignoreClassNames))) {
        handler(event);
      }
    } else if (checkElement(bearingElement, event.target, ignoreClassNames)) {
      handler(event);
    }
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.code === 'Escape' || event.key === 'Esc') {
    const handlerNode = getLastHandlerNode();
    if (handlerNode) {
      handlerNode.handler(event);
    }
  }
};

export const useClickAwayEngine = () => {
  useEffect(() => {
    const documentFallback: DocumentFallback = document;
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (document.addEventListener) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('touchstart', handleClick);
      document.addEventListener('keydown', handleKeyDown);
    } else if (documentFallback.attachEvent) {
      documentFallback.attachEvent(`onclick`, handleClick);
    }

    return () => {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (document.removeEventListener) {
        document.removeEventListener('mousedown', handleClick);
        document.removeEventListener('touchstart', handleClick);
        document.removeEventListener('keydown', handleKeyDown);
      } else if (documentFallback.detachEvent) {
        documentFallback.detachEvent(`onclick`, handleClick);
      }
    };
  }, []);
};
